import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import {
    getAllRolesRequest,
    createRoleRequest,
    updateRoleRequest,
    deleteRoleRequest,
} from '../../../redux/actions/roleActions';
import { getAllModulesRequest } from '../../../redux/actions/moduleActions';
import { getAllUsersRequest, assignRolesRequest } from '../../../redux/actions/userActions';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import './Rolemanagement.css';
import { Pencil, Trash2, ShieldCheck, ChevronDown, ChevronRight, Globe, Layers, UserCheck } from 'lucide-react';
import UserRoleAssignment from './UserRoleAssignment';

/* ─── Role Modal CRUD ───────────────────────────────────── */
const RoleModal = ({ isOpen, onClose, onSubmit, editingRole, loading, modules }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        code: '', name: '', description: '', isActive: true, moduleId: '',
    });

    useEffect(() => {
        if (editingRole) {
            setFormData({
                code: editingRole.code || '',
                name: editingRole.name || '',
                description: editingRole.description || '',
                isActive: editingRole.isActive !== undefined ? editingRole.isActive : true,
                moduleId: editingRole.module ? String(editingRole.module.id) : '',
            });
        } else {
            setFormData({ code: '', name: '', description: '', isActive: true, moduleId: '' });
        }
    }, [editingRole, isOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.code.trim()) { Swal.fire({ icon: 'warning', text: t('Le code du rôle est obligatoire') }); return; }
        if (!formData.name.trim()) { Swal.fire({ icon: 'warning', text: t('Le nom du rôle est obligatoire') }); return; }
        onSubmit({
            code: formData.code.trim(),
            name: formData.name.trim(),
            description: formData.description.trim() || '',
            isActive: !!formData.isActive,
            moduleId: formData.moduleId ? Number(formData.moduleId) : null,
        });
    };

    if (!isOpen) return null;

    return (
        <div className="rm-overlay" onClick={onClose}>
            <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="rm-modal-header">
                    <h3>{editingRole ? t('Modifier le rôle') : t('Ajouter un rôle')}</h3>
                    <button className="rm-modal-close" onClick={onClose} type="button">✕</button>
                </div>
                <form className="rm-form" onSubmit={handleSubmit}>
                    <div className="rm-section-title">{t('Identité')}</div>
                    <div className="rm-grid">
                        <div className="rm-field">
                            <label>{t('Code')} *</label>
                            <input name="code" value={formData.code} onChange={handleChange} required maxLength={50} placeholder="Ex : ROLE-ADMIN" />
                        </div>
                        <div className="rm-field">
                            <label>{t('Nom du rôle')} *</label>
                            <input name="name" value={formData.name} onChange={handleChange} required maxLength={200} placeholder="Ex : Administrateur RH" />
                        </div>
                        <div className="rm-field rm-full">
                            <label>{t('Description')}</label>
                            <textarea name="description" value={formData.description} onChange={handleChange} rows={2} placeholder={t('Description du rôle...')} />
                        </div>
                    </div>
                    <div className="rm-section-title" style={{ marginTop: 14 }}>{t('Portée')}</div>
                    <div className="rm-grid">
                        <div className="rm-field rm-full">
                            <label>{t('Module associé')}</label>
                            <select name="moduleId" value={formData.moduleId} onChange={handleChange}>
                                <option value="">{t('— Global (aucun module) —')}</option>
                                {(modules || []).map((m) => (
                                    <option key={m.id} value={m.id}>{m.name} ({m.code})</option>
                                ))}
                            </select>
                            <span className="rm-field-hint">
                                {formData.moduleId ? t('Rôle spécifique au module sélectionné') : t('Rôle global — accessible sur tous les modules')}
                            </span>
                        </div>
                        <div className="rm-field rm-full">
                            <label className="rm-check-label">
                                <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} />
                                {t('Rôle actif')}
                            </label>
                        </div>
                    </div>
                    <div className="rm-modal-actions">
                        <button type="button" className="rm-btn rm-btn-outline" onClick={onClose} disabled={loading}>{t('Annuler')}</button>
                        <button type="submit" className="rm-btn rm-btn-primary" disabled={loading}>
                            {loading ? t('Chargement...') : editingRole ? t('Enregistrer') : t('Créer le rôle')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ─── Assign Role Modal ─────────────────────────────────── */
const AssignRoleModal = ({ isOpen, onClose, modules, roles, users, onAssign }) => {
    const { t } = useTranslation();
    const [selectedModule, setSelectedModule] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [selectedUser, setSelectedUser] = useState('');

    useEffect(() => {
        if (!isOpen) { setSelectedModule(''); setSelectedRole(''); setSelectedUser(''); }
    }, [isOpen]);

    // Roles filtered by selected module
    const filteredRoles = useMemo(() => {
        if (!selectedModule) return (roles || []).filter(r => r.isGlobal && r.isActive);
        return (roles || []).filter(r => {
            const moduleId = r.module?.id ? String(r.module.id) : '';
            return moduleId === String(selectedModule) && r.isActive;
        });
    }, [selectedModule, roles]);

    // Reset role when module changes
    useEffect(() => { setSelectedRole(''); }, [selectedModule]);

    const handleAssign = () => {
        if (!selectedRole || !selectedUser) {
            Swal.fire({ icon: 'warning', title: t('Champs requis'), text: t('Veuillez sélectionner un utilisateur et un rôle.') });
            return;
        }
        onAssign({ userId: selectedUser, roleId: selectedRole, moduleId: selectedModule || null });
        onClose();
    };

    if (!isOpen) return null;

    const selectedModuleObj = (modules || []).find(m => String(m.id) === String(selectedModule));
    const selectedRoleObj = (roles || []).find(r => String(r.id) === String(selectedRole));

    return (
        <div className="rm-overlay" onClick={onClose}>
            <div className="rm-modal rm-assign-modal" onClick={(e) => e.stopPropagation()}>
                <div className="rm-modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="rm-assign-icon"><UserCheck size={16} /></div>
                        <h3>{t('Affecter un rôle')}</h3>
                    </div>
                    <button className="rm-modal-close" onClick={onClose} type="button">✕</button>
                </div>

                <div className="rm-form">
                    {/* Step 1 – Module */}
                    <div className="rm-assign-step">
                        <div className="rm-assign-step-num">1</div>
                        <div className="rm-assign-step-body">
                            <div className="rm-section-title" style={{ marginBottom: 10 }}>{t('Choisir le module')}</div>
                            <div className="rm-module-grid">
                                <button
                                    type="button"
                                    className={`rm-module-card ${selectedModule === '' ? 'selected' : ''}`}
                                    onClick={() => setSelectedModule('')}
                                >
                                    <Globe size={18} />
                                    <span>{t('Global')}</span>
                                    <small>{t('Tous modules')}</small>
                                </button>
                                {(modules || []).map(m => (
                                    <button
                                        key={m.id}
                                        type="button"
                                        className={`rm-module-card ${String(selectedModule) === String(m.id) ? 'selected' : ''}`}
                                        onClick={() => setSelectedModule(String(m.id))}
                                    >
                                        <Layers size={18} />
                                        <span>{m.name}</span>
                                        <small>{m.code}</small>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Step 2 – Role */}
                    <div className="rm-assign-step">
                        <div className="rm-assign-step-num">2</div>
                        <div className="rm-assign-step-body">
                            <div className="rm-section-title" style={{ marginBottom: 10 }}>
                                {t('Choisir le rôle')}
                                {selectedModuleObj && <span className="rm-step-context"> — {selectedModuleObj.name}</span>}
                                {!selectedModule && <span className="rm-step-context"> — {t('Globaux')}</span>}
                            </div>
                            {filteredRoles.length === 0 ? (
                                <div className="rm-no-roles">{t('Aucun rôle actif dans ce module.')}</div>
                            ) : (
                                <div className="rm-role-chips">
                                    {filteredRoles.map(r => (
                                        <button
                                            key={r.id}
                                            type="button"
                                            className={`rm-role-chip ${String(selectedRole) === String(r.id) ? 'selected' : ''}`}
                                            onClick={() => setSelectedRole(String(r.id))}
                                        >
                                            <span className="rm-chip-code">{r.code}</span>
                                            <span className="rm-chip-name">{r.name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Step 3 – User */}
                    <div className="rm-assign-step">
                        <div className="rm-assign-step-num">3</div>
                        <div className="rm-assign-step-body">
                            <div className="rm-section-title" style={{ marginBottom: 10 }}>{t('Choisir l\'utilisateur')}</div>
                            <select
                                className="rm-user-select"
                                value={selectedUser}
                                onChange={e => setSelectedUser(e.target.value)}
                            >
                                <option value="">{t('— Sélectionner un utilisateur —')}</option>
                                {(users || []).map(u => (
                                    <option key={u.id} value={u.id}>{u.fullName || u.username} ({u.email})</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Summary */}
                    {selectedRole && selectedUser && (
                        <div className="rm-assign-summary">
                            <div className="rm-summary-row">
                                <span className="rm-summary-label">{t('Utilisateur')}</span>
                                <span className="rm-summary-val">
                                    {(users || []).find(u => String(u.id) === String(selectedUser))?.fullName || '—'}
                                </span>
                            </div>
                            <div className="rm-summary-row">
                                <span className="rm-summary-label">{t('Rôle')}</span>
                                <span className="rm-summary-val">{selectedRoleObj?.name || '—'}</span>
                            </div>
                            <div className="rm-summary-row">
                                <span className="rm-summary-label">{t('Module')}</span>
                                <span className="rm-summary-val">{selectedModuleObj?.name || t('Global')}</span>
                            </div>
                        </div>
                    )}

                    <div className="rm-modal-actions">
                        <button type="button" className="rm-btn rm-btn-outline" onClick={onClose}>{t('Annuler')}</button>
                        <button type="button" className="rm-btn rm-btn-primary" onClick={handleAssign} disabled={!selectedRole || !selectedUser}>
                            <UserCheck size={14} />
                            {t('Affecter le rôle')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─── Module Group Card ─────────────────────────────────── */
const ModuleGroup = ({ moduleName, moduleCode, roles, onEdit, onDelete, isGlobal, t }) => {
    const [open, setOpen] = useState(true);

    return (
        <div className={`rm-group ${isGlobal ? 'rm-group-global' : ''}`}>
            <button type="button" className="rm-group-header" onClick={() => setOpen(o => !o)}>
                <div className="rm-group-left">
                    {isGlobal ? <Globe size={15} className="rm-group-icon global" /> : <Layers size={15} className="rm-group-icon module" />}
                    <span className="rm-group-name">{moduleName}</span>
                    {moduleCode && <span className="rm-group-code">{moduleCode}</span>}
                    <span className="rm-group-count">{roles.length} rôle{roles.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="rm-group-chevron">
                    {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                </div>
            </button>

            {open && (
                <div className="rm-group-body">
                    {roles.length === 0 ? (
                        <div className="rm-group-empty">{t('Aucun rôle dans ce groupe')}</div>
                    ) : (
                        <div className="rm-role-cards">
                            {roles.map(r => (
                                <div key={r.id} className={`rm-role-card ${!r.isActive ? 'inactive' : ''}`}>
                                    <div className="rm-role-card-top">
                                        <span className="rm-code">{r.code}</span>
                                        <div className="rm-role-card-actions">
                                            <button className="rm-link-btn" onClick={() => onEdit(r)} title={t('Modifier')} type="button">
                                                <Pencil size={13} />
                                            </button>
                                            <button className="rm-link-btn danger" onClick={() => onDelete(r.id, r.name)} title={t('Supprimer')} type="button">
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="rm-role-card-name">{r.name}</div>
                                    {r.description && <div className="rm-role-card-desc">{r.description}</div>}
                                    <div className="rm-role-card-footer">
                                        <span className={`rm-toggle ${r.isActive ? 'on' : 'off'}`}>
                                            <span className="rm-toggle-knob" />
                                            <span>{r.isActive ? t('Actif') : t('Inactif')}</span>
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/* ─── Main ──────────────────────────────────────────────── */
const RoleManagement = ({
                            roles, loading, message, error, modules, users,
                            getAllRoles, createRole, updateRole, deleteRole, getAllModules, getAllUsers, assignRoles,
                        }) => {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAssignOpen, setIsAssignOpen] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [viewMode, setViewMode] = useState('modules'); // 'modules' | 'table'
    const [activeTab, setActiveTab] = useState('roles');

    useEffect(() => { getAllRoles(); getAllModules(); getAllUsers(); }, [getAllRoles, getAllModules, getAllUsers]);

    const openCreate = () => { setEditingRole(null); setIsModalOpen(true); };
    const openEdit = (r) => { setEditingRole(r); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); setEditingRole(null); };

    const handleSubmit = (formData) => {
        if (editingRole) updateRole(editingRole.id, formData);
        else createRole(formData);
        closeModal();
    };

    const handleDelete = (id, name) => {
        Swal.fire({
            title: t('Supprimer'),
            text: `${t('Supprimer le rôle')} "${name}" ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6b7280',
            confirmButtonText: t('Oui, supprimer'),
            cancelButtonText: t('Annuler'),
        }).then((res) => { if (res.isConfirmed) deleteRole(id); });
    };

    const handleAssign = ({ userId, roleId, moduleId }) => {
        assignRoles(userId, [Number(roleId)]);
    };

    const filtered = useMemo(() => {
        return (roles || []).filter((r) => {
            const q = searchTerm.toLowerCase().trim();
            const matchSearch = !q ||
                (r.code || '').toLowerCase().includes(q) ||
                (r.name || '').toLowerCase().includes(q) ||
                (r.description || '').toLowerCase().includes(q);
            const matchActive = activeFilter === 'all' ? true : activeFilter === 'active' ? !!r.isActive : !r.isActive;
            return matchSearch && matchActive;
        });
    }, [roles, searchTerm, activeFilter]);

    // Group roles by module for card view
    const grouped = useMemo(() => {
        const globalRoles = filtered.filter(r => r.isGlobal || !r.module);
        const byModule = {};
        filtered.filter(r => !r.isGlobal && r.module).forEach(r => {
            const key = String(r.module.id);
            if (!byModule[key]) byModule[key] = { module: r.module, roles: [] };
            byModule[key].roles.push(r);
        });
        return { globalRoles, byModule };
    }, [filtered]);

    return (
        <div className="rm-page">
            <div className="rm-main-tabs">
                <button className={`rm-main-tab ${activeTab === 'roles' ? 'active' : ''}`} onClick={() => setActiveTab('roles')} type="button">
                    {t('Gestion des Rôles')}
                </button>
                <button className={`rm-main-tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')} type="button">
                    {t('Affectation Utilisateurs')}
                </button>
            </div>

            {activeTab === 'roles' ? (
                <div className="rm-content">
                    {/* Header */}
                    <div className="rm-header" style={{ marginTop: 0 }}>
                        <div>
                            <h2>
                                <ShieldCheck size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                {t('Gestion des rôles')}
                            </h2>
                            <p className="rm-subtitle">{t('Gérez les rôles et leurs permissions par module')}</p>
                        </div>
                        <div className="rm-header-actions">
                            <button className="rm-btn rm-btn-assign" onClick={() => setIsAssignOpen(true)} type="button">
                                <UserCheck size={14} />
                                {t('Affecter un rôle')}
                            </button>
                            <button className="rm-btn rm-btn-primary" onClick={openCreate} type="button">
                                + {t('Ajouter un rôle')}
                            </button>
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="rm-toolbar">
                        <input
                            className="rm-search"
                            placeholder={t('Rechercher (code, nom...)')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />

                        <div className="rm-filter-group">
                            <span className="rm-filter-label">{t('Statut')}</span>
                            <div className="rm-filter-tabs">
                                {[{ key: 'all', label: t('Tous') }, { key: 'active', label: t('Actifs') }, { key: 'inactive', label: t('Inactifs') }].map(f => (
                                    <button key={f.key} className={`rm-filter-tab ${activeFilter === f.key ? 'active' : ''}`} onClick={() => setActiveFilter(f.key)} type="button">{f.label}</button>
                                ))}
                            </div>
                        </div>

                        {/* View toggle */}
                        <div className="rm-filter-group" style={{ marginLeft: 'auto' }}>
                            <span className="rm-filter-label">{t('Vue')}</span>
                            <div className="rm-filter-tabs">
                                <button className={`rm-filter-tab ${viewMode === 'modules' ? 'active' : ''}`} onClick={() => setViewMode('modules')} type="button">
                                    <Layers size={11} style={{ marginRight: 4 }} />{t('Modules')}
                                </button>
                                <button className={`rm-filter-tab ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')} type="button">
                                    ☰ {t('Tableau')}
                                </button>
                            </div>
                        </div>

                        <div className="rm-meta">
                            {loading ? t('Chargement...') : `${filtered.length} rôle${filtered.length !== 1 ? 's' : ''}`}
                        </div>
                    </div>

                    {message && <div className="rm-alert rm-alert-success">{message}</div>}
                    {error && <div className="rm-alert rm-alert-danger">{error}</div>}

                    {/* Content */}
                    {loading ? (
                        <div className="rm-loading" style={{ margin: '32px 18px' }}><div className="rm-spinner" /><span>{t('Chargement...')}</span></div>
                    ) : viewMode === 'modules' ? (
                        /* ── Module Card View ── */
                        <div className="rm-groups-container">
                            {filtered.length === 0 ? (
                                <div className="rm-empty"><p>{t('Aucun rôle trouvé')}</p></div>
                            ) : (
                                <>
                                    {/* Global roles */}
                                    {grouped.globalRoles.length > 0 && (
                                        <ModuleGroup
                                            moduleName={t('Rôles Globaux')}
                                            moduleCode={null}
                                            roles={grouped.globalRoles}
                                            onEdit={openEdit}
                                            onDelete={handleDelete}
                                            isGlobal={true}
                                            t={t}
                                        />
                                    )}
                                    {/* Per-module groups */}
                                    {Object.values(grouped.byModule).map(({ module, roles: mRoles }) => (
                                        <ModuleGroup
                                            key={module.id}
                                            moduleName={module.name}
                                            moduleCode={module.code}
                                            roles={mRoles}
                                            onEdit={openEdit}
                                            onDelete={handleDelete}
                                            isGlobal={false}
                                            t={t}
                                        />
                                    ))}
                                </>
                            )}
                        </div>
                    ) : (
                        /* ── Table View ── */
                        <div className="rm-table-wrapper">
                            {filtered.length === 0 ? (
                                <div className="rm-empty"><p>{t('Aucun rôle trouvé')}</p></div>
                            ) : (
                                <table className="rm-table">
                                    <thead>
                                    <tr>
                                        <th>{t('Code')}</th>
                                        <th>{t('Nom')}</th>
                                        <th>{t('Module')}</th>
                                        <th>{t('Portée')}</th>
                                        <th>{t('Statut')}</th>
                                        <th style={{ width: 100, textAlign: 'right' }}>{t('Actions')}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filtered.map((r) => (
                                        <tr key={r.id}>
                                            <td><span className="rm-code">{r.code}</span></td>
                                            <td>
                                                <div className="rm-name">{r.name}</div>
                                                {r.description && <div className="rm-desc">{r.description}</div>}
                                            </td>
                                            <td>
                                                {r.moduleName
                                                    ? <span className="rm-module-badge">{r.moduleName}</span>
                                                    : <span className="rm-muted">—</span>}
                                            </td>
                                            <td>
                                                {r.isGlobal
                                                    ? <span className="rm-scope-badge global">{t('Global')}</span>
                                                    : <span className="rm-scope-badge module">{t('Module')}</span>}
                                            </td>
                                            <td>
                                                <button type="button" className={`rm-toggle ${r.isActive ? 'on' : 'off'}`}>
                                                    <span className="rm-toggle-knob" />
                                                    <span>{r.isActive ? t('Actif') : t('Inactif')}</span>
                                                </button>
                                            </td>
                                            <td className="rm-row-actions">
                                                <button className="rm-link-btn" onClick={() => openEdit(r)} title={t('Modifier')} type="button"><Pencil size={15} /></button>
                                                <button className="rm-link-btn danger" onClick={() => handleDelete(r.id, r.name)} title={t('Supprimer')} type="button"><Trash2 size={15} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    <RoleModal isOpen={isModalOpen} onClose={closeModal} onSubmit={handleSubmit} editingRole={editingRole} loading={loading} modules={modules} />
                    <AssignRoleModal isOpen={isAssignOpen} onClose={() => setIsAssignOpen(false)} modules={modules} roles={roles} users={users} onAssign={handleAssign} />
                </div>
            ) : (
                <UserRoleAssignment />
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    roles: state.role.roles || [],
    loading: state.role.loading,
    message: state.role.message,
    error: state.role.error,
    modules: state.module.modules || [],
    users: state.user.users || [],
});

const mapDispatchToProps = (dispatch) => ({
    getAllRoles: () => dispatch(getAllRolesRequest()),
    createRole: (data) => dispatch(createRoleRequest(data)),
    updateRole: (id, data) => dispatch(updateRoleRequest(id, data)),
    deleteRole: (id) => dispatch(deleteRoleRequest(id)),
    getAllModules: () => dispatch(getAllModulesRequest()),
    getAllUsers: () => dispatch(getAllUsersRequest()),
    assignRoles: (userId, roleIds) => dispatch(assignRolesRequest(userId, roleIds)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RoleManagement);
